export const JAB_ORG_ID = "332a9d47-6e36-11e8-a392-061714f61e3e";
// Read this mutable identity catalog from S3 directly. The public-assets
// CloudFront distribution currently ignores query strings, so a cache-busting
// URL there can still return an older customer mapping.
export const JAB_CUSTOMERS_ASSET_URL = "https://assets-ambifi-com.s3.amazonaws.com/public/projects/jab/customers/customers.json";

function normalizeIdentityValue(value) {
	return value === undefined || value === null ? "" : String(value).trim();
}

function normalizeIdentityKey(value) {
	return normalizeIdentityValue(value).toLocaleLowerCase();
}

export function normalizeCustomerName(value) {
	let normalized = normalizeIdentityValue(value);
	if (normalized && typeof normalized.normalize === "function") {
		normalized = normalized.normalize("NFKC");
	}
	return normalized.replace(/\s+/g, " ").toLocaleLowerCase();
}

function getLegacyBlankHeaderCustomerCode(customer) {
	if (!customer || typeof customer !== "object") {
		return "";
	}

	const blankHeaderKey = Object.keys(customer).find((key) => (
		key.trim() === "" && normalizeIdentityValue(customer[key]) !== ""
	));
	return blankHeaderKey ? normalizeIdentityValue(customer[blankHeaderKey]) : "";
}

export function getCustomerCode(customer = {}) {
	return [
		customer.customerCode,
		customer.customerId,
		customer.companyCode,
		getLegacyBlankHeaderCustomerCode(customer)
	].map(normalizeIdentityValue).find(Boolean) || "";
}

export function normalizeCustomer(customer = {}) {
	if (!customer || typeof customer !== "object" || Array.isArray(customer)) {
		return null;
	}

	const customerGuid = normalizeIdentityValue(customer.customerGuid);
	const customerCode = getCustomerCode(customer);
	const customerName = normalizeIdentityValue(customer.customerName || customer.company);

	if (!customerGuid && !customerCode && !customerName) {
		return null;
	}

	return {
		...customer,
		customerGuid,
		customerCode,
		customerName
	};
}

export function normalizeCustomers(customers) {
	if (!Array.isArray(customers)) {
		return [];
	}
	return customers.map(normalizeCustomer).filter(Boolean);
}

export function getCustomerSelectValue(customer) {
	const normalizedCustomer = normalizeCustomer(customer);
	if (!normalizedCustomer) {
		return "";
	}
	if (normalizedCustomer.customerGuid) {
		return `guid:${normalizeIdentityKey(normalizedCustomer.customerGuid)}`;
	}
	if (normalizedCustomer.customerCode) {
		return `code:${normalizeIdentityKey(normalizedCustomer.customerCode)}`;
	}
	return normalizedCustomer.customerName ? `name:${normalizeCustomerName(normalizedCustomer.customerName)}` : "";
}

export function buildCustomerSelectOptions(customers) {
	return normalizeCustomers(customers)
		.map((customer) => ({
			value: getCustomerSelectValue(customer),
			label: [customer.customerCode, customer.customerName].filter(Boolean).join(" - "),
			customer
		}))
		.filter((option) => option.value && option.label)
		.sort((left, right) => left.label.localeCompare(right.label));
}

function addToIndex(index, key, customer) {
	if (!key) {
		return;
	}
	if (!index[key]) {
		index[key] = [];
	}
	index[key].push(customer);
}

export function buildCustomerIdentityMaps(customers) {
	const normalizedCustomers = normalizeCustomers(customers);
	const byGuid = {};
	const byCode = {};
	const byName = {};
	const customerMap = {};

	normalizedCustomers.forEach((customer) => {
		addToIndex(byGuid, normalizeIdentityKey(customer.customerGuid), customer);
		addToIndex(byCode, normalizeIdentityKey(customer.customerCode), customer);
		addToIndex(byName, normalizeCustomerName(customer.customerName), customer);
		if (customer.customerName) {
			customerMap[customer.customerName] = customer;
		}
	});

	return {
		customers: normalizedCustomers,
		byGuid,
		byCode,
		byName,
		customerMap
	};
}

function getIdentityValue(identity, keys) {
	return keys.map((key) => identity && identity[key])
		.map(normalizeIdentityValue)
		.find(Boolean) || "";
}

export function hasCustomerIdentity(identity = {}) {
	return Boolean(
		getIdentityValue(identity, ["customerGuid"])
		|| getIdentityValue(identity, ["customerCode", "customerId", "companyCode"])
		|| getIdentityValue(identity, ["company", "customerName"])
	);
}

export function shouldBlockUnresolvedJabCustomerSave({
	isJab,
	customerSelectionValue,
	hasUnresolvedSavedCustomerIdentity,
	company,
	customerGuid,
	customerCode
} = {}) {
	return Boolean(
		isJab
		&& !customerSelectionValue
		&& (
			hasUnresolvedSavedCustomerIdentity
			|| hasCustomerIdentity({ company, customerGuid, customerCode })
		)
	);
}

export function resolveCustomerIdentity(customersOrMaps, identity = {}) {
	const maps = Array.isArray(customersOrMaps)
		? buildCustomerIdentityMaps(customersOrMaps)
		: customersOrMaps;
	const resolveAttempt = (attempt) => {
		const matches = attempt.index && attempt.index[attempt.normalize(attempt.value)]
			? attempt.index[attempt.normalize(attempt.value)]
			: [];
		if (matches.length === 1) {
			return {
				status: "resolved",
				customer: matches[0],
				matchedBy: attempt.field,
				matchedValue: attempt.value
			};
		}
		if (matches.length > 1) {
			return {
				status: "ambiguous",
				customer: null,
				matchedBy: attempt.field,
				matchedLabel: attempt.label,
				matchedValue: attempt.value,
				matches
			};
		}
		return {
			status: "unresolved",
			customer: null,
			matchedBy: attempt.field,
			matchedLabel: attempt.label,
			matchedValue: attempt.value
		};
	};
	const guidAttempt = {
		field: "customerGuid",
		label: "customer GUID",
		value: getIdentityValue(identity, ["customerGuid"]),
		index: maps && maps.byGuid,
		normalize: normalizeIdentityKey
	};
	const codeAttempt = {
		field: "customerCode",
		label: "customer code",
		value: getIdentityValue(identity, ["customerCode", "customerId", "companyCode"]),
		index: maps && maps.byCode,
		normalize: normalizeIdentityKey
	};
	const nameAttempt = {
		field: "company",
		label: "customer name",
		value: getIdentityValue(identity, ["company", "customerName"]),
		index: maps && maps.byName,
		normalize: normalizeCustomerName
	};

	if (guidAttempt.value) {
		const guidResult = resolveAttempt(guidAttempt);
		if (guidResult.status !== "resolved") {
			return guidResult;
		}
		if (codeAttempt.value) {
			const codeResult = resolveAttempt(codeAttempt);
			if (codeResult.status !== "resolved") {
				return codeResult;
			}
			if (getCustomerSelectValue(codeResult.customer) !== getCustomerSelectValue(guidResult.customer)) {
				return {
					status: "conflict",
					customer: null,
					guidCustomer: guidResult.customer,
					codeCustomer: codeResult.customer
				};
			}
		}
		return guidResult;
	}

	if (codeAttempt.value) {
		return resolveAttempt(codeAttempt);
	}

	if (nameAttempt.value) {
		return resolveAttempt(nameAttempt);
	}

	return {
		status: "unresolved",
		customer: null,
		matchedBy: null,
		matchedValue: ""
	};
}

function getCustomerIdentityDescription(identity = {}) {
	const customerGuid = getIdentityValue(identity, ["customerGuid"]);
	const customerCode = getIdentityValue(identity, ["customerCode", "customerId", "companyCode"]);
	const customerName = getIdentityValue(identity, ["company", "customerName"]);
	if (customerCode && customerName) {
		return `${customerCode} - ${customerName}`;
	}
	return customerCode || customerName || customerGuid || "the selected job";
}

export function resolveCustomerIdentityOrThrow(customersOrMaps, identity = {}) {
	const result = resolveCustomerIdentity(customersOrMaps, identity);
	if (result.status === "resolved") {
		return result.customer;
	}
	if (result.status === "conflict") {
		const error = new Error("The saved customer GUID and customer code identify different customers. Select the customer again in the member's Member Info before creating this invoice.");
		error.code = "CONFLICTING_INVOICE_CUSTOMER";
		throw error;
	}

	if (result.status === "ambiguous") {
		const error = new Error(`More than one current customer matches ${result.matchedLabel} "${result.matchedValue}". Correct the duplicate customer records before creating this invoice.`);
		error.code = "AMBIGUOUS_INVOICE_CUSTOMER";
		throw error;
	}

	const description = getCustomerIdentityDescription(identity);
	const error = new Error(`The invoice customer (${description}) could not be matched to the current customer list. Select the customer in the member's Member Info and try again.`);
	error.code = "UNRESOLVED_INVOICE_CUSTOMER";
	throw error;
}

export function resolveInvoiceCustomerIdentityOrThrow(customersOrMaps, instances = []) {
	const invoiceInstances = Array.isArray(instances) && instances.length > 0 ? instances : [{}];
	const resolvedCustomers = invoiceInstances.map((instance) => (
		resolveCustomerIdentityOrThrow(customersOrMaps, instance)
	));
	const resolvedCustomerKeys = resolvedCustomers.reduce((keys, customer) => {
		const key = getCustomerSelectValue(customer);
		if (key && keys.indexOf(key) === -1) {
			keys.push(key);
		}
		return keys;
	}, []);

	if (resolvedCustomerKeys.length !== 1) {
		const customerLabels = resolvedCustomers
			.map((customer) => [customer.customerCode, customer.customerName].filter(Boolean).join(" - "))
			.filter((label, index, labels) => label && labels.indexOf(label) === index);
		const error = new Error(`The selected jobs resolve to more than one current customer${customerLabels.length > 0 ? ` (${customerLabels.join(", ")})` : ""}. Create a separate invoice for each customer.`);
		error.code = "MULTIPLE_INVOICE_CUSTOMERS";
		throw error;
	}

	return resolvedCustomers[0];
}

export function getJabCustomersAssetUrl(cacheBust = Date.now()) {
	return `${JAB_CUSTOMERS_ASSET_URL}?v=${encodeURIComponent(String(cacheBust))}`;
}

export async function fetchFreshJabCustomers(fetchImpl = fetch, cacheBust = Date.now()) {
	const response = await fetchImpl(getJabCustomersAssetUrl(cacheBust), {
		method: "GET",
		cache: "no-store"
	});
	if (!response || !response.ok) {
		const status = response && response.status ? ` (${response.status})` : "";
		throw new Error(`The current customer list could not be loaded${status}. Please try again before creating the invoice.`);
	}

	const responseJson = await response.json();
	if (!Array.isArray(responseJson)) {
		throw new Error("The current customer list is invalid. Please republish the customer sheet before creating the invoice.");
	}
	return normalizeCustomers(responseJson);
}

export function isJabOrganization(orgId) {
	return orgId === JAB_ORG_ID;
}
